/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Segmented } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { segmentedToggleStyle } from 'src/styles/segmentedToggleStyle'
import Text from '../Text'

export interface SegmentedInputOption<V extends string> {
  label: string
  value: V
}

interface SegmentedInputProps<V extends string> {
  /** Already Title-cased by the caller. */
  label: string
  options: SegmentedInputOption<V>[]
  value: V
  onChange: (value: V) => void
  /** A helper line under the toggle. */
  hint?: string
  disabled?: boolean
}

/**
 * A labelled house segmented toggle (`segmentedToggleStyle`) for picking one
 * of a few options, laid out like the other form fields: label, control, hint.
 * Fills its field's width like a text input and wraps when its labels don't
 * fit, so it never runs out of a narrow column.
 */
export const SegmentedInput = <V extends string>({
  label,
  options,
  value,
  onChange,
  hint,
  disabled = false,
}: SegmentedInputProps<V>) => (
  <div css={cellStyle}>
    <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
      {label}
    </Text>
    <Segmented
      block
      css={[segmentedToggleStyle, fillStyle]}
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={(next) => onChange(next as V)}
      options={options}
    />
    {hint ? (
      <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
        {hint}
      </Text>
    ) : null}
  </div>
)

const cellStyle = css`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  min-width: 0;
`

/**
 * Over the house style: fill the field, sharing the width by label length, and
 * wrap to a second row rather than cut a label when the options don't fit.
 */
const fillStyle = css`
  align-self: stretch;
  .ant-segmented-group {
    flex-wrap: wrap;
    row-gap: 2px;
  }
  &.ant-segmented-block .ant-segmented-item {
    flex: 1 1 auto;
    min-width: max-content;
  }
  .ant-segmented-item-label {
    padding: 0 8px;
  }
`
